import React, { useEffect, useState, useRef } from 'react';
import { Landmark } from '@mediapipe/tasks-vision';
import { PostureBaseline } from '../../lib/emotion-detection';
import './PostureCalibration.scss';

interface PostureCalibrationProps {
    landmarks: { face: Landmark[][], pose: Landmark[][] };
    videoStream: MediaStream | null;
    onComplete: (baseline: PostureBaseline) => void;
}

export const PostureCalibration: React.FC<PostureCalibrationProps> = ({ landmarks, videoStream, onComplete }) => {
    const [step, setStep] = useState<'countdown' | 'recording' | 'finished'>('countdown');
    const [countdown, setCountdown] = useState(3);
    const [recordingProgress, setRecordingProgress] = useState(0);

    const videoRef = useRef<HTMLVideoElement>(null);
    const recordingRef = useRef<PostureBaseline[]>([]);

    // Mount the video stream
    useEffect(() => {
        if (videoRef.current && videoStream) {
            videoRef.current.srcObject = videoStream;
        }
    }, [videoStream]);

    // Handle Countdown Logic
    useEffect(() => {
        let timer: number;
        if (step === 'countdown') {
            if (countdown > 0) {
                timer = window.setTimeout(() => setCountdown(c => c - 1), 1000);
            } else {
                setStep('recording');
            }
        }
        return () => clearTimeout(timer);
    }, [step, countdown]);

    // Handle Recording Logic - Fixed 3 seconds
    const landmarksRef = useRef(landmarks);
    useEffect(() => {
        landmarksRef.current = landmarks;
    }, [landmarks]);

    useEffect(() => {
        if (step === 'recording') {
            const startTime = Date.now();
            const duration = 3000;

            const interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const progress = (elapsed / duration) * 100;
                setRecordingProgress(Math.min(100, progress));

                const currentLandmarks = landmarksRef.current;
                if (currentLandmarks.pose && currentLandmarks.pose.length > 0) {
                    const l = currentLandmarks.pose[0];
                    const nose = l[0];
                    const lEar = l[7];
                    const rEar = l[8];
                    const lSh = l[11];
                    const rSh = l[12];

                    if (nose && lEar && rEar && lSh && rSh) {
                        recordingRef.current.push({
                            avgShoulderY: (lSh.y + rSh.y) / 2,
                            avgEarY: (lEar.y + rEar.y) / 2,
                            avgNoseY: nose.y,
                            shoulderWidth: Math.abs(lSh.x - rSh.x)
                        });
                    }
                }

                if (elapsed >= duration) {
                    clearInterval(interval);
                    finishCalibration();
                }
            }, 50); // High frequency sampling for 3s

            return () => clearInterval(interval);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const finishCalibration = () => {
        if (recordingRef.current.length === 0) {
            alert("Vücut noktaları algılanamadı. Lütfen kameraya tam göründüğünüzden emin olun.");
            setStep('countdown');
            setCountdown(3);
            return;
        }

        const count = recordingRef.current.length;
        const avg = recordingRef.current.reduce((acc, curr) => ({
            avgShoulderY: acc.avgShoulderY + curr.avgShoulderY / count,
            avgEarY: acc.avgEarY + curr.avgEarY / count,
            avgNoseY: acc.avgNoseY + curr.avgNoseY / count,
            shoulderWidth: acc.shoulderWidth + curr.shoulderWidth / count,
        }), { avgShoulderY: 0, avgEarY: 0, avgNoseY: 0, shoulderWidth: 0 });

        setStep('finished');
        setTimeout(() => onComplete(avg), 1500);
    };

    return (
        <div className="posture-calibration-overlay">
            <div className="calibration-modal">
                <div className="video-preview-container">
                    <video ref={videoRef} autoPlay playsInline />
                    <div className="overlay-content">
                        {step === 'countdown' && <div className="timer">{countdown}</div>}
                    </div>
                </div>

                {step === 'countdown' && (
                    <div className="countdown-view">
                        <h2>Lütfen Dik Durun</h2>
                        <p>Kalibrasyon {countdown} saniye içinde başlıyor...</p>
                    </div>
                )}

                {step === 'recording' && (
                    <div className="recording-view">
                        <h2>Dik Durun ve Bekleyin</h2>
                        <p>Duruşunuz 3 saniye boyunca analiz ediliyor...</p>
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${recordingProgress}%` }} />
                        </div>
                    </div>
                )}

                {step === 'finished' && (
                    <div className="finished-view">
                        <h2>Kalibrasyon Tamamlandı!</h2>
                        <span className="material-symbols-outlined success-icon">check_circle</span>
                    </div>
                )}
            </div>
        </div>
    );
};
